<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import type {CardsModuleProgress, StudyMode} from '@shared/types'
import {clientApi} from '@/api'
import {useLocalStore} from '@/stores/local'
import {useTranslationsStore} from '@/stores/translations'
import {useUserStore} from '@/stores/user'
import {useLessonProgressStore} from '@/stores/lessonProgress'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import CircularProgress from '@/components/CircularProgress.vue'

const emit = defineEmits<{close: []}>()
const router = useRouter()
const route = useRoute()
const localStore = useLocalStore()
const userStore = useUserStore()
const lessonProgressStore = useLessonProgressStore()
const translationsStore = useTranslationsStore()

const levels = computed(() => [...new Set(lessonProgressStore.indexUnits.map((u) => u.level))].sort((a, b) => a - b))

function unitsOf(level: number): number[] {
  return lessonProgressStore.indexUnits
    .filter((u) => u.level === level)
    .map((u) => u.unit)
    .sort((a, b) => a - b)
}

function moduleName(level: number, unit: number): string {
  return `cards.modules.level${level}.module${unit}`
}

function modulePercent(mod: CardsModuleProgress | null): number {
  if (!mod) return 0
  const total = mod.listen.m + mod.translate.m
  if (!total) return 0
  return ((mod.listen.n + mod.translate.n) / total) * 100
}

function sidePercent(side: {n: number; m: number}): string {
  return side.m ? ((side.n / side.m) * 100).toFixed(2) : '0.00'
}

function moduleTooltipValues(mod: CardsModuleProgress | null) {
  const listen = mod?.listen ?? {n: 0, m: 0}
  const translate = mod?.translate ?? {n: 0, m: 0}
  const front = sidePercent(listen)
  const back = sidePercent(translate)
  return {
    average: ((Number(front) + Number(back)) / 2).toFixed(2),
    front,
    frontN: listen.n,
    frontM: listen.m,
    back,
    backN: translate.n,
    backM: translate.m,
  }
}

const currentMode = computed<StudyMode>(() =>
  route.params.mode === 'translate' || route.params.mode === 'listen' ? route.params.mode : localStore.cardsMode
)

const activeModule = computed(() => {
  const level = Number(route.params.level)
  const unit = Number(route.params.unit)
  if (!Number.isInteger(level) || !Number.isInteger(unit)) return null
  return {level, unit}
})

const isNextActive = computed(() => route.query.source === 'next')

const nextProgress = ref<{listen: {n: number; m: number}; translate: {n: number; m: number}} | null>(null)

async function loadNextProgress() {
  const profile = userStore.profile
  if (!profile || !localStore.profileId) {
    nextProgress.value = null
    return
  }
  try {
    const base = {
      profile_id: localStore.profileId,
      targetLanguage: profile.settings.target_language,
      nativeLanguage: profile.settings.native_language,
      source: 'next',
      limit: 1,
    }
    const [listen, translate] = await Promise.all([
      clientApi.cards.getDeck({...base, mode: 'listen'}),
      clientApi.cards.getDeck({...base, mode: 'translate'}),
    ])
    nextProgress.value = {
      listen: {n: listen.progress.n, m: listen.progress.m},
      translate: {n: translate.progress.n, m: translate.progress.m},
    }
  } catch (error) {
    console.error('Error loading next deck progress:', error)
  }
}

function nextPercent(): number {
  if (!nextProgress.value) return 0
  const total = nextProgress.value.listen.m + nextProgress.value.translate.m
  if (!total) return 0
  return ((nextProgress.value.listen.n + nextProgress.value.translate.n) / total) * 100
}

function nextTooltipValues() {
  const listen = nextProgress.value?.listen ?? {n: 0, m: 0}
  const translate = nextProgress.value?.translate ?? {n: 0, m: 0}
  const front = sidePercent(listen)
  const back = sidePercent(translate)
  return {
    average: ((Number(front) + Number(back)) / 2).toFixed(2),
    front,
    frontN: listen.n,
    frontM: listen.m,
    back,
    backN: translate.n,
    backM: translate.m,
  }
}

async function loadProgress() {
  const profile = userStore.profile
  if (!profile || !localStore.profileId) return
  try {
    await lessonProgressStore.load(
      localStore.profileId,
      profile.settings.target_language,
      profile.settings.native_language
    )
  } catch (error) {
    console.error('Error loading lesson progress:', error)
  }
  void loadNextProgress()
}

watch(
  () => [
    localStore.profileId,
    userStore.profile?.settings.target_language,
    userStore.profile?.settings.native_language,
    route.path,
  ],
  loadProgress,
  {immediate: true}
)

function go(level: number, unit: number) {
  router.push(`/cards/${currentMode.value}/${level}/${unit}`)
  emit('close')
}

function goNext() {
  router.push({path: `/cards/${currentMode.value}`, query: {source: 'next'}})
  emit('close')
}
</script>
<template>
  <SidebarGroup>
    <SidebarGroupContent>
      <SidebarMenu>
        <SidebarMenuItem>
          <div class="flex w-full items-center group-data-[collapsible=icon]:hidden">
            <SidebarMenuButton
              :is-active="isNextActive"
              class="min-w-0 flex-1"
              data-testid="cards-next-deck"
              @click="goNext">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <CircularProgress :percent="nextPercent()" class="shrink-0 text-primary" />
                  </TooltipTrigger>
                  <TooltipContent class="whitespace-pre-line">{{
                    $t('cards.moduleProgressTooltip', nextTooltipValues())
                  }}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span class="truncate">{{ $t('cards.newCards') }}</span>
            </SidebarMenuButton>
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
      <div v-for="level in levels" :key="level" class="mb-1">
        <SidebarGroupLabel class="text-xs text-muted-foreground"
          >{{ $t('common.level') }} {{ translationsStore.arabicToRomanNumber(level) }}</SidebarGroupLabel
        >
        <SidebarMenu>
          <SidebarMenuItem v-for="unit in unitsOf(level)" :key="`${level}-${unit}`">
            <div class="flex w-full items-center group-data-[collapsible=icon]:hidden">
              <SidebarMenuButton
                :is-active="activeModule?.level === level && activeModule?.unit === unit"
                class="min-w-0 flex-1"
                :data-testid="`cards-module-${level}-${unit}`"
                @click="go(level, unit)">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <CircularProgress
                        :percent="modulePercent(lessonProgressStore.moduleProgressFor(level, unit))"
                        class="shrink-0 text-primary" />
                    </TooltipTrigger>
                    <TooltipContent class="whitespace-pre-line">{{
                      $t(
                        'cards.moduleProgressTooltip',
                        moduleTooltipValues(lessonProgressStore.moduleProgressFor(level, unit))
                      )
                    }}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span class="truncate">{{ $t(moduleName(level, unit)) }}</span>
              </SidebarMenuButton>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>
    </SidebarGroupContent>
  </SidebarGroup>
</template>
